import { gql } from '@apollo/client';

// ============ QUERIES ============

/**
 * Get all time entries for a work order
 */
export const GET_WORK_ORDER_TIME_ENTRIES = gql`
  query GetWorkOrderTimeEntries($workOrderId: ID!) {
    workOrderTimeEntries(workOrderId: $workOrderId) {
      id
      workOrderId
      userId
      startedAt
      endedAt
      durationMinutes
      isActive
      notes
      arrivedAtLatitude
      arrivedAtLongitude
      arrivedAtAccuracy
      departedAtLatitude
      departedAtLongitude
      departedAtAccuracy
      createdAt
      user {
        id
        firstName
        lastName
        role
      }
    }
  }
`;

/**
 * Get active time entry for current user on a work order
 */
export const GET_ACTIVE_TIME_ENTRY = gql`
  query GetActiveTimeEntry($workOrderId: ID!) {
    activeTimeEntry(workOrderId: $workOrderId) {
      id
      workOrderId
      userId
      startedAt
      endedAt
      durationMinutes
      isActive
      notes
      arrivedAtLatitude
      arrivedAtLongitude
      arrivedAtAccuracy
      departedAtLatitude
      departedAtLongitude
      departedAtAccuracy
      user {
        id
        firstName
        lastName
      }
    }
  }
`;

/**
 * Get total time worked on a work order (all users)
 */
export const GET_TOTAL_TIME_WORKED = gql`
  query GetTotalTimeWorked($workOrderId: ID!) {
    totalTimeWorked(workOrderId: $workOrderId)
  }
`;

/**
 * Get time worked by current user on a work order
 */
export const GET_MY_TIME_WORKED = gql`
  query GetMyTimeWorked($workOrderId: ID!) {
    myTimeWorked(workOrderId: $workOrderId)
  }
`;

/**
 * Get time summary grouped by user for a work order
 */
export const GET_TIME_SUMMARY_BY_USER = gql`
  query GetTimeSummaryByUser($workOrderId: ID!) {
    timeSummaryByUser(workOrderId: $workOrderId) {
      userId
      userName
      totalMinutes
    }
  }
`;

/**
 * Get today's GPS tracking data (latest position per team member for map view)
 */
export const GET_TODAY_GPS_TRACKING = gql`
  query GetTodayGPSTracking {
    todayGPSTracking {
      id
      workOrderId
      userId
      startedAt
      endedAt
      durationMinutes
      isActive
      arrivedAtLatitude
      arrivedAtLongitude
      arrivedAtAccuracy
      departedAtLatitude
      departedAtLongitude
      departedAtAccuracy
      user {
        id
        firstName
        lastName
      }
      workOrder {
        id
        description
        property {
          id
          name
          address
          city
          state
          zip
        }
      }
    }
  }
`;

/**
 * Get all GPS tracking entries for today (paginated for list view)
 */
export const GET_TODAY_GPS_TRACKING_ALL = gql`
  query GetTodayGPSTrackingAll($skip: Int, $take: Int) {
    todayGPSTrackingAll(skip: $skip, take: $take) {
      entries {
        id
        workOrderId
        userId
        startedAt
        endedAt
        durationMinutes
        isActive
        arrivedAtLatitude
        arrivedAtLongitude
        arrivedAtAccuracy
        departedAtLatitude
        departedAtLongitude
        departedAtAccuracy
        user {
          id
          firstName
          lastName
        }
        workOrder {
          id
          description
          property {
            id
            name
            address
            city
            state
            zip
          }
        }
      }
      total
    }
  }
`;

// ============ MUTATIONS ============

/**
 * Start tracking time on a work order
 */
export const START_TIME_ENTRY = gql`
  mutation StartTimeEntry($input: StartTimeEntryInput!) {
    startTimeEntry(input: $input) {
      id
      workOrderId
      userId
      startedAt
      isActive
      notes
      arrivedAtLatitude
      arrivedAtLongitude
      arrivedAtAccuracy
      user {
        id
        firstName
        lastName
      }
    }
  }
`;

/**
 * Stop an active time entry
 */
export const STOP_TIME_ENTRY = gql`
  mutation StopTimeEntry($input: StopTimeEntryInput!) {
    stopTimeEntry(input: $input) {
      id
      workOrderId
      userId
      startedAt
      endedAt
      durationMinutes
      isActive
      notes
      arrivedAtLatitude
      arrivedAtLongitude
      arrivedAtAccuracy
      departedAtLatitude
      departedAtLongitude
      departedAtAccuracy
      user {
        id
        firstName
        lastName
      }
    }
  }
`;

/**
 * Pause time tracking (alias for stop)
 */
export const PAUSE_TIME_ENTRY = gql`
  mutation PauseTimeEntry($timeEntryId: ID!) {
    pauseTimeEntry(timeEntryId: $timeEntryId) {
      id
      workOrderId
      startedAt
      endedAt
      durationMinutes
      isActive
    }
  }
`;

/**
 * Resume time tracking
 */
export const RESUME_TIME_ENTRY = gql`
  mutation ResumeTimeEntry($workOrderId: ID!) {
    resumeTimeEntry(workOrderId: $workOrderId) {
      id
      workOrderId
      startedAt
      isActive
      user {
        id
        firstName
        lastName
      }
    }
  }
`;

/**
 * Update a time entry (manual adjustment)
 */
export const UPDATE_TIME_ENTRY = gql`
  mutation UpdateTimeEntry($input: UpdateTimeEntryInput!) {
    updateTimeEntry(input: $input) {
      id
      workOrderId
      userId
      startedAt
      endedAt
      durationMinutes
      isActive
      notes
      user {
        id
        firstName
        lastName
      }
    }
  }
`;

/**
 * Add a manual time entry
 */
export const ADD_MANUAL_TIME_ENTRY = gql`
  mutation AddManualTimeEntry($input: AddManualTimeEntryInput!) {
    addManualTimeEntry(input: $input) {
      id
      workOrderId
      userId
      startedAt
      endedAt
      durationMinutes
      isActive
      notes
      user {
        id
        firstName
        lastName
      }
    }
  }
`;

/**
 * Delete a time entry
 */
export const DELETE_TIME_ENTRY = gql`
  mutation DeleteTimeEntry($timeEntryId: ID!) {
    deleteTimeEntry(timeEntryId: $timeEntryId)
  }
`;

/**
 * Stop all active time entries for current user on a work order
 */
export const STOP_ALL_MY_ACTIVE_TIME_ENTRIES = gql`
  mutation StopAllMyActiveTimeEntries($workOrderId: ID!) {
    stopAllMyActiveTimeEntries(workOrderId: $workOrderId) {
      id
      endedAt
      durationMinutes
      isActive
    }
  }
`;

